import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  IconButton,
  Rating,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Seo from "../components/Seo";
import TrustPanel from "../components/TrustPanel";
import { SITE } from "../data/site";
import { spreadsheetReviews } from "../data/reviews";
import { buildBreadcrumbSchema, buildServiceSchema, buildWebPageSchema } from "../utils/schema";
import "./Services.css";

const image1 = `${process.env.PUBLIC_URL}/assets/spreadsheetsExample1.png`;
const image2 = `${process.env.PUBLIC_URL}/assets/spreadsheetsExample2.png`;
const image3 = `${process.env.PUBLIC_URL}/assets/spreadsheetsExample3.png`;
const heroBackgroundImage = `${process.env.PUBLIC_URL}/assets/Logo.svg`;

function ImageCarousel({ images }) {
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (!Array.isArray(images)) return;

    images.forEach((img) => {
      const preloader = new Image();
      preloader.src = img.image;
    });
  }, [images]);

  if (!images || images.length === 0) return null;

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  return (
    <Paper className="bws-gsl-carousel" elevation={0}>
      <div className="bws-gsl-carouselInner">
        <IconButton className="bws-gsl-carouselBtn" onClick={prev} aria-label="Previous example">
          <ChevronLeftIcon />
        </IconButton>

        <div className="bws-gsl-carouselFrame">
          {images.map((img, i) => (
            <img
              key={i}
              className={`bws-gsl-carouselImg ${i === idx ? "is-active" : "is-hidden"}`}
              src={img.image}
              alt={img.altText}
              loading="eager"
              decoding="async"
            />
          ))}
        </div>

        <IconButton className="bws-gsl-carouselBtn" onClick={next} aria-label="Next example">
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className="bws-gsl-carouselDots">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`bws-gsl-dot ${i === idx ? "is-active" : ""}`}
            aria-label={`View example ${i + 1}`}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
    </Paper>
  );
}

export default function Spreadsheets() {
  const safeReviews = Array.isArray(spreadsheetReviews) ? spreadsheetReviews : [];
  const title = "Spreadsheet Help — Excel, Google Sheets, CSV Cleanup, Dashboards & Automation";
  const description =
    "Spreadsheet help for Excel, Google Sheets, CSV files, dashboards, formulas, cleanup, reporting, and automation. Practical support for businesses and individuals.";
  const canonical = "https://brainwavesolutions.net/services/spreadsheets";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "Services", url: `${SITE.url}/services` },
      { name: "Spreadsheets", url: canonical },
    ]),
    buildServiceSchema({
      name: "Spreadsheet Help",
      description,
      canonical,
      offerLabel: "Starting at $25",
      serviceType: "Spreadsheet support for Excel and Google Sheets",
    }),
  ];

  const images = [
    {
      image: image1,
      altText:
        "Spreadsheet dashboard showing income versus expenses chart, summary tables, and organized reporting sections.",
    },
    {
      image: image2,
      altText:
        "Spreadsheet workflow tracker with project details, task sections, and structured reporting layout.",
    },
    {
      image: image3,
      altText:
        "Spreadsheet cost tracking example showing labor, meals, lodging, and total expense summaries.",
    },
  ].filter((img) => img.image);

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        structuredData={structuredData}
      />

      <Box className="bws-gsl-page">
        <Container maxWidth="lg">
          <div className="bws-gsl-hero">
            <div
              className="bws-gsl-heroMain"
              style={{ "--gsl-bg-image": `url(${heroBackgroundImage})` }}
            >
              <Typography variant="h1" className="bws-gsl-title">
                Spreadsheet help for Excel, Google Sheets, CSV files, dashboards, and reporting
              </Typography>

              <p className="bws-gsl-lead">
                Spreadsheets are supposed to make the work easier, but once they grow more complex they often become
                the source of the problem instead. Formulas break, totals stop matching, exports need cleanup, reports
                take too long, and nobody wants to be the person who touches the wrong cell and causes a mess.
              </p>

              <p className="bws-gsl-lead bws-gsl-leadTight">
                I help businesses and individuals fix spreadsheet problems, clean up messy files, build dashboards,
                organize data, and automate recurring work across Excel, Google Sheets, and CSV-based workflows. The
                goal is simple: make the spreadsheet accurate, easier to use, and less stressful to work with.
              </p>

              <div className="bws-gsl-badges">
                <span className="bws-gsl-badge">Excel &amp; Google Sheets</span>
                <span className="bws-gsl-badge">Formulas &amp; dashboards</span>
                <span className="bws-gsl-badge">CSV cleanup</span>
                <span className="bws-gsl-badge">Reporting &amp; automation</span>
              </div>

              <div className="bws-gsl-ctaRow">
                <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
                  Get spreadsheet help
                </Button>
              </div>

              <div className="bws-gsl-miniTrust">
                <span>Businesses &amp; individuals</span>
                <span>•</span>
                <span>Clear communication</span>
                <span>•</span>
                <span>Practical solutions</span>
              </div>
            </div>

            <div className="bws-gsl-sideStack">
              <TrustPanel
                title="Work directly with Nate"
                body="You’re not sending your project into a big agency pipeline, you will work directly with me."
                badges={["Based in the USA", "Native English speaker"]}
              />

              <Paper className="bws-gsl-sideCard" elevation={0}>
                <Typography className="bws-gsl-sideTitle">Common spreadsheet problems I solve</Typography>
                <ul className="bws-gsl-sideList">
                  <li>Broken formulas, mismatched totals, and fragile logic</li>
                  <li>Messy spreadsheets that are hard to trust or maintain</li>
                  <li>CSV files that need cleanup before they can be used</li>
                  <li>Dashboards and reports that need to be clearer and faster</li>
                  <li>Recurring spreadsheet work that should be automated</li>
                </ul>

                <Typography className="bws-gsl-sideNote">
                  Best first step: send a message with a description of the file, what is not working, and what you want it to do better.
                </Typography>
              </Paper>
            </div>
          </div>

          <div className="bws-gsl-strip">
            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Fix what’s broken</Typography>
              <Typography className="bws-gsl-stripText">
                Correct bad formulas, clean up messy logic, and make the numbers easier to trust.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Make it easier to use</Typography>
              <Typography className="bws-gsl-stripText">
                Improve layout, structure, and reporting so the file is clearer for everyone working with it.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Save time on repeat work</Typography>
              <Typography className="bws-gsl-stripText">
                Reduce manual steps with dashboards, rollups, cleanup workflows, and automation where it helps.
              </Typography>
            </Paper>
          </div>

          {images.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Examples:</Typography>
                <p className="bws-gsl-p">
                  Examples of spreadsheet dashboards, workflow trackers, and reporting layouts.
                </p>
              </div>

              <ImageCarousel images={images} />
            </div>
          )}

          <div className="bws-gsl-section">
            <div className="bws-gsl-grid">
              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">What I help with</Typography>
                  <ul className="bws-gsl-list">
                    <li>Excel and Google Sheets support</li>
                    <li>Formula troubleshooting and spreadsheet cleanup</li>
                    <li>CSV cleanup, formatting, and preparation</li>
                    <li>Dashboards, trackers, and reporting sheets</li>
                    <li>Automation for recurring spreadsheet workflows</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">Good fit for</Typography>
                  <ul className="bws-gsl-list">
                    <li>Businesses relying on spreadsheets for operations or reporting</li>
                    <li>People stuck on a spreadsheet issue they cannot solve</li>
                    <li>Teams dealing with messy exports or manual reporting work</li>
                    <li>Anyone who needs cleaner, more reliable spreadsheet workflows</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {safeReviews.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Client feedback</Typography>
              </div>

              <div className="bws-gsl-reviews">
                {safeReviews.slice(0, 3).map((r, idx) => (
                  <Paper key={idx} className="bws-gsl-review" elevation={0}>
                    <Rating
                      value={5}
                      readOnly
                      size="small"
                      className="bws-gsl-reviewStars"
                      aria-label="5 out of 5 stars"
                    />
                    <Typography className="bws-gsl-reviewQuote">“{r.quote}”</Typography>
                    <Typography className="bws-gsl-reviewAuthor">— {r.author}</Typography>
                  </Paper>
                ))}
              </div>
            </div>
          )}

          <div className="bws-gsl-section">
            <Paper className="bws-gsl-faq" elevation={0}>
              <Typography className="bws-gsl-h2">Frequently asked questions</Typography>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Do you work with both Excel and Google Sheets?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. I help with both, and I can also help clean up CSV files and other spreadsheet-based workflows.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you help with one small issue, or does it need to be a full project?</Typography>
                <Typography className="bws-gsl-faqA">
                  Both. Some requests are quick fixes, and some grow into larger cleanup, reporting, or automation work.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">What do you need from me to get started?</Typography>
                <Typography className="bws-gsl-faqA">
                  A short description of the problem, the file type, and what you want the spreadsheet to do. A screenshot or sample file helps a lot.
                </Typography>
              </div>
            </Paper>
          </div>

          <Paper className="bws-gsl-bottomCta" elevation={0}>
            <div>
              <Typography className="bws-ctaTitle">Need help with a spreadsheet?</Typography>
              <p className="bws-muted">
                Send a quick description of what is not working and what you want the file to do better.
              </p>
            </div>
            <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
